import { useState } from "react";

import { Accordion } from "@eisox/design-system";
import { useAppForm } from "@eisox/form";
import { getDirtyValues } from "@eisox/tools";
import { z } from "@eisox/zod";
import { revalidateLogic } from "@tanstack/react-form";
import { createFileRoute } from "@tanstack/react-router";

import {
  getOpenedSetpointSection,
  getSetpointErrorSections,
  getSetpointFormValues,
  getSetpointQuantityTitle,
  getTemperatureCouples,
  hasGeneralSetpoints,
  hasSetpointQuantity,
  SETPOINT_QUANTITIES,
  SETPOINT_SECTIONS,
  setpointFormSchema,
  SetpointGeneralSection,
  SetpointInputMode,
  SetpointQuantitySection,
  SetpointTemperatureSection,
  toSetpointPatch,
  useAirHandlerUnitContext,
  useUpdateAirHandlerUnit,
  type SetpointSection,
} from "~/features/air-handler-unit";
import { m } from "~/paraglide/messages";

export const Route = createFileRoute(
  "/_authenticated/buildings_/$buildingId/air-handler-units/$airHandlerUnitId/settings/setpoint",
)({
  validateSearch: z.object({
    section: z.enum(SETPOINT_SECTIONS).optional().catch(undefined),
  }),
  component: RouteComponent,
});

const scrollIntoView = (node: HTMLDivElement | null) => node?.scrollIntoView({ block: "start" });

function RouteComponent() {
  const { buildingId } = Route.useParams();
  const { section } = Route.useSearch();

  const { airHandlerUnit } = useAirHandlerUnitContext(Route.id);

  const { mutateAsync } = useUpdateAirHandlerUnit(buildingId);

  const [inputMode, setInputMode] = useState<SetpointInputMode>(SetpointInputMode.SETPOINT);
  const [toggledSections, setToggledSections] = useState<string[]>();

  const primaryUnit = airHandlerUnit[0];

  const form = useAppForm({
    defaultValues: getSetpointFormValues(primaryUnit),
    validationLogic: revalidateLogic(),
    validators: { onDynamic: setpointFormSchema },
    onSubmitMeta: { openedSections: [] as string[] },
    onSubmitInvalid: ({ formApi, meta }) => {
      const errorSections = getSetpointErrorSections(Object.keys(formApi.getAllErrors().fields));
      setToggledSections([...new Set([...meta.openedSections, ...errorSections])]);
    },
    onSubmit: async ({ formApi, value }) => {
      const dirtyValues = getDirtyValues(formApi.options.defaultValues, value);
      if (!dirtyValues) return;
      // Each failed PATCH raises its own error dialog; keep the form dirty unless every unit succeeded.
      const results = await Promise.allSettled(
        airHandlerUnit.map(ahu =>
          mutateAsync({ airHandlerUnitId: ahu.id, body: toSetpointPatch(dirtyValues, ahu.flows) }),
        ),
      );
      if (results.every(result => result.status === "fulfilled")) formApi.reset(value);
    },
  });

  if (!primaryUnit) return null;

  const generalDisplayed = hasGeneralSetpoints(primaryUnit);
  const temperatureDisplayed = getTemperatureCouples(primaryUnit).length > 0;
  const quantities = SETPOINT_QUANTITIES.filter(quantity => hasSetpointQuantity(quantity, primaryUnit));
  const displayedSections: SetpointSection[] = [
    ...(generalDisplayed ? (["general"] as const) : []),
    ...(temperatureDisplayed ? (["temperature"] as const) : []),
    ...quantities,
  ];
  const openedSection = getOpenedSetpointSection(displayedSections, section);
  const openedSections = toggledSections ?? (openedSection ? [openedSection] : []);
  const sectionRef = (value: SetpointSection) => (value === section ? scrollIntoView : undefined);

  return (
    <form
      className="gap-5 flex h-full w-full flex-col overflow-auto"
      onSubmit={e => {
        e.preventDefault();
        void form.handleSubmit({ openedSections });
      }}
    >
      <form.AppForm>
        <Accordion.Root
          className="max-w-105 w-full"
          type="multiple"
          value={openedSections}
          onValueChange={setToggledSections}
        >
          {generalDisplayed && (
            <Accordion.Item ref={sectionRef("general")} value="general">
              <Accordion.Trigger className="text-primary-blue">{m.general_and_regulation()}</Accordion.Trigger>
              <Accordion.Content>
                <SetpointGeneralSection form={form} airHandlerUnit={primaryUnit} />
              </Accordion.Content>
            </Accordion.Item>
          )}
          {temperatureDisplayed && (
            <Accordion.Item ref={sectionRef("temperature")} value="temperature">
              <Accordion.Trigger className="text-primary-blue">{m.temperature()}</Accordion.Trigger>
              <Accordion.Content>
                <SetpointTemperatureSection
                  form={form}
                  airHandlerUnit={primaryUnit}
                  inputMode={inputMode}
                  onInputModeChange={setInputMode}
                />
              </Accordion.Content>
            </Accordion.Item>
          )}
          {quantities.map(quantity => (
            <Accordion.Item key={quantity} ref={sectionRef(quantity)} value={quantity}>
              <Accordion.Trigger className="text-primary-blue">{getSetpointQuantityTitle(quantity)}</Accordion.Trigger>
              <Accordion.Content>
                <SetpointQuantitySection form={form} quantity={quantity} airHandlerUnit={primaryUnit} />
              </Accordion.Content>
            </Accordion.Item>
          ))}
        </Accordion.Root>
        <div className="mt-auto flex items-center justify-center">
          <form.SubmitButton className="mt-auto self-center" label={m.save()} />
        </div>
      </form.AppForm>
    </form>
  );
}
